import { createRoadMap, deleteRoadMap, lockRoadmapById, updateRoadMap } from '@/domain/roadmap/api';
import { ROADMAP_BASIC_KEY, ROADMAP_KEY } from '@/domain/roadmap/hooks';
import { type BasicRoadmap, type RoadmapListDetail, type RoadMapUpdate } from '@/domain/roadmap/types';
import { type ApiError } from '@/services/ApiError';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { useRoadmapEditStore } from '../store/roadmap';
import { invalidateRoadmapQueries, patchRoadmapListCaches } from '../utils';
import { initialValue } from '../utils/initialValue';

const onMutateError = (error: ApiError) => {
  toast.error(`An error occurred while saving data! ${error.message}`, {
    position: 'top-right',
    autoClose: false,
  });
};

export const useRoadmapActions = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: mutateDelete, isPending: isDeleting } = useMutation({
    mutationFn: deleteRoadMap,
    onSuccess: (id: number) => {
      toast.success('RoadMap Deleted');
      queryClient.setQueriesData<RoadmapListDetail[]>(
        { queryKey: ['roadmap', ROADMAP_KEY] },
        (old) => old?.filter((r) => r.idRoadmap !== id),
      );
      queryClient.setQueriesData<BasicRoadmap[]>(
        { queryKey: ['roadmap', ROADMAP_BASIC_KEY] },
        (old) => old?.filter((r) => r.idRoadmap !== id),
      );
      queryClient.removeQueries({ queryKey: ['roadmap', 'editable', id] });
    },
    onError: (error: Error) => {
      toast.error(`An error occurred while saving data! ${error?.message}`);
    },
  });

  const { mutateAsync: mutateLock, isPending: isLocking } = useMutation({
    mutationFn: lockRoadmapById,
    onSuccess: (_idRoadmap: number, variables: { idRoadmap: number; value: boolean }) => {
      queryClient.setQueriesData<RoadmapListDetail[]>(
        { queryKey: ['roadmap', ROADMAP_KEY] },
        (old) => old?.map((r) => (r.idRoadmap === variables.idRoadmap ? { ...r, locked: variables.value } : r)),
      );
      queryClient.setQueryData<RoadMapUpdate>(['roadmap', 'editable', variables.idRoadmap], (old) =>
        old ? { ...old, locked: variables.value } : old,
      );
    },
    onError: (error: Error) => {
      toast.error(`An error occurred while saving data! ${error?.message}`);
    },
  });

  const { mutateAsync: mutateUpdate, isPending: isLoadingUpdate } = useMutation({
    mutationFn: updateRoadMap,
    onSuccess: (saved: RoadMapUpdate) => {
      useRoadmapEditStore.setState({ editableRoadMap: initialValue });
      toast.success('Data saved successfully');
      patchRoadmapListCaches(queryClient, saved);
      queryClient.setQueryData<RoadMapUpdate>(['roadmap', 'editable', saved.idRoadmap], saved);
    },
    onError: onMutateError,
  });

  const { mutateAsync: mutateCreate, isPending: isLoadingCreate } = useMutation({
    mutationFn: createRoadMap,
    onSuccess: () => {
      useRoadmapEditStore.setState({ editableRoadMap: initialValue });
      toast.success('Data saved successfully');
      invalidateRoadmapQueries(queryClient);
    },
    onError: onMutateError,
  });

  return {
    deleteRoadmap: mutateDelete,
    isDeleting,
    lockRoadmap: mutateLock,
    isLocking,
    updateRoadmap: mutateUpdate,
    isLoadingUpdate,
    createRoadmap: mutateCreate,
    isLoadingCreate,
  };
};
